import cx from "classnames";
import { Location } from "history";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";

import { AdSlot, getDownscaledSize, sanitizeSize } from "@/mk2/containers/AdSlot/AdSlot";
import { adSlotAnchorLeaderboardCloseByUser } from "@/mk2/containers/AdSlotAnchorLeaderboard/AdSlotAnchorLeaderboard.actions";
import { getAdSlotAnchorLeaderboardState } from "@/mk2/containers/AdSlotAnchorLeaderboard/AdSlotAnchorLeaderboard.selectors";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import useEventCallback from "@/mk2/hooks/useEventCallback";
import { AnchorLeaderboardProps } from "@/mk2/pages/Page";
import { AppState } from "@/mk2/reducers";
import { UserEntity } from "@/mk2/schemas";
import { CLOSE_STICKY_RECTANGLE } from "@/mk/autogenerated/translations/AdSlotAnchorLeaderboard.1adf669c1fb74f9a9453089f6dcee513";
import { hasAds } from "@/mk/bazaar/common/userUtils";

import styles from "./AdSlotAnchorLeaderboard.mscss";

export const PREFIX_AD_SLOT_ANCHOR_LEADERBOARD = "mk-stickyrectangle";

const DEFAULT_SCROLL_THRESHOLD = 10;
const DEFAULT_SHOW_DELAY = 2000; // 2 seconds

const DEFAULT_ALLOW_DOWNSCALING = true;

export enum AnchorLeaderboardShowTrigger {
    OnDelayExpired,
    OnScroll,
    Instantly,
}

export interface OwnProps extends AnchorLeaderboardProps {
    requestUser: UserEntity;
    location: Location;
    showTrigger: AnchorLeaderboardShowTrigger;
    allowDownscaling?: boolean;
}

interface StateProps {
    // render elements of AdSlotAnchorLeaderboard if adSlotRenderEnded is true
    adSlotSize: number[] | string;
    adSlotRenderEnded: boolean;
    adSlotClosedByUser: boolean;
}

interface DispatchProps {
    onAdSlotAnchorLeaderboardCloseByUser(closed: boolean);
}

type Props = OwnProps & StateProps & DispatchProps;

const AdSlotAnchorLeaderboard: React.FC<Props> = ({
    adSlotRenderEnded,
    adSlotClosedByUser,
    zone,
    targeting,
    location,
    requestUser,
    adSlotSize,
    onAdSlotAnchorLeaderboardCloseByUser,
    showTrigger = AnchorLeaderboardShowTrigger.OnDelayExpired,
    allowDownscaling = DEFAULT_ALLOW_DOWNSCALING,
}) => {
    const [timeoutExpired, setTimeoutExpired] = useState(false);
    const [scrolledEnough, setScrolledEnough] = useState(false);
    const [closedByUserCounter, setClosedByUserCounter] = useState(0);

    // Add refs and state for dimensions
    const adSlotRef = useRef<HTMLDivElement>(null);
    const [parentWidth, setParentWidth] = useState(0);

    const onAdSlotAnchorLeaderboardCloseByUserCallback = useEventCallback((closed: boolean) => {
        onAdSlotAnchorLeaderboardCloseByUser(closed);
        if (closed) {
            if (closedByUserCounter === 0) {
                // Reopen once after 30 seconds
                setTimeout(() => {
                    onAdSlotAnchorLeaderboardCloseByUser(false);
                }, 30000); // 30 seconds
            }
            setClosedByUserCounter((prevCounter) => prevCounter + 1);
        }
    }, []);

    useEffect(() => {
        onAdSlotAnchorLeaderboardCloseByUserCallback(false);

        setTimeoutExpired(false);
        setScrolledEnough(false);

        const timer = setTimeout(() => {
            setTimeoutExpired(true);
        }, DEFAULT_SHOW_DELAY);

        const handleScroll = () => {
            if (window.scrollY > DEFAULT_SCROLL_THRESHOLD) {
                setScrolledEnough(true);
                window.removeEventListener("scroll", handleScroll);
            }
        };
        window.addEventListener("scroll", handleScroll, { passive: true });

        return () => {
            clearTimeout(timer);
            window.removeEventListener("scroll", handleScroll);
        };
    }, [zone, location?.key]);

    // Measure the size after render
    useEffect(() => {
        if (adSlotRenderEnded) {
            const container = document.getElementById(
                `${PREFIX_AD_SLOT_ANCHOR_LEADERBOARD}-container`,
            );
            if (container) {
                const { offsetWidth } = container;
                setParentWidth(offsetWidth);
            }
        }
    }, [adSlotRenderEnded, zone, location?.key]);

    const handleClose = () => {
        onAdSlotAnchorLeaderboardCloseByUserCallback(true);
    };

    const canRender =
        (showTrigger === AnchorLeaderboardShowTrigger.OnScroll && scrolledEnough) ||
        (showTrigger === AnchorLeaderboardShowTrigger.OnDelayExpired && timeoutExpired) ||
        showTrigger === AnchorLeaderboardShowTrigger.Instantly;

    const hidden = adSlotClosedByUser || !hasAds(requestUser) || !canRender;

    let stylesForScaledAd = {};
    if (allowDownscaling) {
        const { adSlotWidth, adSlotHeight } = sanitizeSize(adSlotSize);
        const { scaledWidth, scaledHeight, scale } = getDownscaledSize(
            parentWidth,
            adSlotWidth,
            adSlotHeight,
        );
        if (scale !== 1) {
            stylesForScaledAd = {
                overflow: "hidden",
                width: `${scaledWidth}px`,
                height: `${scaledHeight}px`,
                margin: "auto",
                boxShadow: "0px 0px 6px -2px #000000",
            };
        }
    }

    return (
        <div
            id={`${PREFIX_AD_SLOT_ANCHOR_LEADERBOARD}-container`}
            className={cx(
                styles.AdSlotAnchorLeaderboard,
                adSlotRenderEnded && styles["AdSlotAnchorLeaderboard--rendered"],
            )}
        >
            {!hidden && adSlotRenderEnded && (
                <div className={styles.AdSlotAnchorLeaderboard__tabs}>
                    <div
                        className={styles.AdSlotAnchorLeaderboard__tabs__tab}
                        onClick={handleClose}
                    >
                        {CLOSE_STICKY_RECTANGLE}
                    </div>
                </div>
            )}
            {!hidden && (
                <div
                    className={cx(
                        styles.AdSlotAnchorLeaderboard__ad,
                        adSlotRenderEnded && styles["AdSlotAnchorLeaderboard__ad--rendered"],
                    )}
                    ref={adSlotRef}
                    style={stylesForScaledAd}
                >
                    <AdSlot
                        zone={zone}
                        prefix={PREFIX_AD_SLOT_ANCHOR_LEADERBOARD}
                        targeting={targeting}
                        location={location}
                        parentWidth={parentWidth}
                        allowDownscaling={allowDownscaling}
                    />
                </div>
            )}
        </div>
    );
};

function mapStateToProps(state: AppState, _ownProps: OwnProps): StateProps {
    const substate = getAdSlotAnchorLeaderboardState(state);
    return {
        adSlotSize: substate.adSlotSize,
        adSlotRenderEnded: substate.adSlotRenderEnded,
        adSlotClosedByUser: substate.adSlotClosedByUser,
    };
}

const mapDispatchToProps: MapDispatchToPropsObject<DispatchProps> = {
    onAdSlotAnchorLeaderboardCloseByUser: adSlotAnchorLeaderboardCloseByUser,
};

export default connect<StateProps, DispatchProps, OwnProps>(
    mapStateToProps,
    mapDispatchToProps,
)(AdSlotAnchorLeaderboard);
